import { zodResolver } from '@hookform/resolvers/zod';
import { UserRoundXIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { CreateOrderDTO, CustomerDTO } from '@invenco/sales-interface/interfaces';

import { Button } from '@/components/core/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { WizardStep } from '@/components/wizard';
import type { WizardStepDefinition } from '@/components/wizard/wizard-context';

import {
  CustomerFields,
  customerFieldsDefaultValues,
  customerFieldsSchema,
  type CustomerFieldsSubmit,
  type CustomerFieldsValues,
  getCustomerFieldsValues,
} from '../../customer-fields';
import { CustomerCombobox } from './customer-combobox';

type CustomerStepMode = 'search' | 'details';

export function CustomerStep({
  step,
  onChange,
}: {
  step: WizardStepDefinition;
  onChange: (input: Partial<CreateOrderDTO>) => void;
}) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<CustomerStepMode>('search');
  const form = useForm<CustomerFieldsValues, unknown, CustomerFieldsSubmit>({
    resolver: zodResolver(customerFieldsSchema),
    mode: 'onChange',
    defaultValues: customerFieldsDefaultValues,
  });

  const showSearch = () => {
    setMode('search');
    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const showCustomer = (customer: CustomerDTO) => {
    form.reset(getCustomerFieldsValues(customer));
    setMode('details');
    void form.trigger();
  };

  const showCreateCustomer = () => {
    form.reset(customerFieldsDefaultValues);
    setMode('details');
  };

  const submitSection = async () => {
    if (mode !== 'details') {
      showSearch();
      return false;
    }

    let valid = false;

    await form.handleSubmit((data) => {
      onChange({
        customerId: data.customerId,
        customerName: data.customerName,
        email: data.email,
        phone: data.phone,
        shippingAddress: data.shippingAddress,
        billingAddress: data.billingAddress,
      });
      valid = true;
    })();

    return valid;
  };

  return (
    <WizardStep
      step={step}
      complete={mode === 'details' && form.formState.isValid}
      onSubmitStep={submitSection}
    >
      <Card aria-label="Customer">
        <CardHeader>
          <CardTitle>Customer</CardTitle>
          {mode === 'details' && (
            <CardAction>
              <Button type="button" variant="ghost" size="sm" onClick={showSearch}>
                <UserRoundXIcon />
                Change
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          {mode === 'search' ? (
            <CustomerCombobox
              inputRef={searchInputRef}
              onSelect={showCustomer}
              onCreateNewCustomer={showCreateCustomer}
            />
          ) : (
            <Form {...form}>
              <form className="grid gap-4">
                <CustomerFields />
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </WizardStep>
  );
}
